import { create } from 'zustand';

import {
  BookContent,
  BookConfig,
  PageInfo,
  BookProgress,
  ViewSettings,
  TimeInfo,
  FIXED_LAYOUT_FORMATS,
} from '@/types/book';
import { Insets } from '@/types/misc';
import { EnvConfigType } from '@/services/environment';
import { FoliateView } from '@/types/view';
import { isAbsEbook } from '@/utils/audiobook';
import { DocumentLoader, TOCItem } from '@/libs/document';
import {
  isPseStreamFileName,
  openPseStreamBook,
  parsePseStreamFileName,
} from '@/services/opds/pseStream';
import type { FileSystem } from '@/types/system';
import { isFeedBookUrl, parseFeedBookUrl } from '@/services/rss/feedBookUrl';
import { openFeedBookDoc } from '@/services/rss/feedReader';
import { computeBookNav, hydrateBookNav, isBookNavCacheCurrent, updateToc } from '@/services/nav';
import {
  formatTitle,
  getMetadataHash,
  getPrimaryLanguage,
  parseSeriesPosition,
} from '@/utils/book';
import { getBaseFilename } from '@/utils/path';
import { SUPPORTED_LANGNAMES } from '@/services/constants';
import { useSettingsStore } from './settingsStore';
import { BookData, useBookDataStore } from './bookDataStore';
import { useLibraryStore } from './libraryStore';
import { clearBookProgress, getBookProgress, setBookProgress } from './readerProgressStore';
import { uniqueId } from '@/utils/misc';
import { getWidePages, type WidePagesOptions } from '@/utils/spread';

interface ViewState {
  /* Unique key for each book view */
  key: string;
  view: FoliateView | null;
  viewerKey: string;
  isPrimary: boolean;
  loading: boolean;
  inited: boolean;
  error: string | null;
  /* `progress` moved to readerProgressStore — see that file's header for
     rationale. Use `useBookProgress(key)` for reactive subscription or
     `getBookProgress(key)` for one-shot reads. */
  ribbonVisible: boolean;
  ttsEnabled: boolean;
  /* True while an Auto Scroll session (#4998) is engaged for this view;
     session-only, never persisted. Drives the View menu checkmark. */
  autoScrollEnabled: boolean;
  /* True while drawing mode is on for this view; session-only. */
  drawingEnabled: boolean;
  syncing: boolean;
  gridInsets: Insets | null;
  /* True while the reader is showing a position requested by an external
     deep link (e.g. ?cfi=...) that the user hasn't yet confirmed by reading.
     Progress writers (auto-save, cloud sync, kosync) skip while this is true
     so the user's actual last-read position isn't overwritten by a preview.
     Cleared on the first user-initiated relocate (page turn / scroll). */
  previewMode: boolean;
  /* View settings for the view:
    generally view settings have a hierarchy of global settings < book settings < view settings
    view settings for primary view are saved to book config which is persisted to config file
    omitting settings that are not changed from global settings */
  viewSettings: ViewSettings | null;
}

interface ReaderStore {
  viewStates: { [key: string]: ViewState };
  bookKeys: string[];
  hoveredBookKey: string | null;
  /* The action tab selected in the mobile bottom bar (font/color/progress);
     lives here rather than in FooterBar state so the TTS mini player can
     stack above the expanded panel. Persists across bar hide/show. */
  bottomBarTab: string;
  setBookKeys: (keys: string[]) => void;
  setHoveredBookKey: (key: string | null) => void;
  setBottomBarTab: (tab: string) => void;
  setBookmarkRibbonVisibility: (key: string, visible: boolean) => void;
  setTTSEnabled: (key: string, enabled: boolean) => void;
  setDrawingEnabled: (key: string, enabled: boolean) => void;
  setAutoScrollEnabled: (key: string, enabled: boolean) => void;
  setIsLoading: (key: string, loading: boolean) => void;
  setIsSyncing: (key: string, syncing: boolean) => void;
  setProgress: (
    key: string,
    location: string,
    tocItem: TOCItem,
    pageItem: BookProgress['pageItem'],
    section: PageInfo,
    pageinfo: PageInfo,
    timeinfo: TimeInfo,
    range: Range,
    fraction: number,
  ) => void;
  getProgress: (key: string) => BookProgress | null;
  setView: (key: string, view: FoliateView) => void;
  getView: (key: string | null) => FoliateView | null;
  getViews: () => FoliateView[];
  getViewsById: (id: string) => FoliateView[];
  setViewSettings: (key: string, viewSettings: ViewSettings) => void;
  getViewSettings: (key: string) => ViewSettings | null;

  initViewState: (
    envConfig: EnvConfigType,
    id: string,
    key: string,
    isPrimary?: boolean,
    reload?: boolean,
  ) => Promise<void>;
  clearViewState: (key: string) => void;
  getViewState: (key: string) => ViewState | null;
  areBooksOpen: (ids: string[]) => boolean;
  getGridInsets: (key: string) => Insets | null;
  setGridInsets: (key: string, insets: Insets | null) => void;
  setViewInited: (key: string, inited: boolean) => void;
  setPreviewMode: (key: string, previewMode: boolean) => void;
  recreateViewer: (envConfig: EnvConfigType, key: string) => void;
}

export const useReaderStore = create<ReaderStore>((set, get) => ({
  viewStates: {},
  bookKeys: [],
  hoveredBookKey: null,
  bottomBarTab: '',
  setBookKeys: (keys: string[]) => set({ bookKeys: keys }),
  setHoveredBookKey: (key: string | null) => set({ hoveredBookKey: key }),
  setBottomBarTab: (tab: string) => set({ bottomBarTab: tab }),
  getView: (key: string | null) => (key && get().viewStates[key]?.view) || null,
  setView: (key: string, view) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: { ...state.viewStates[key]!, view },
      },
    })),
  getViews: () => Object.values(get().viewStates).map((state) => state.view!),
  getViewsById: (id: string) => {
    const { viewStates } = get();
    return Object.values(viewStates)
      .filter((state) => state.key && state.key.startsWith(id))
      .map((state) => state.view!);
  },

  clearViewState: (key: string) => {
    // Drop the per-book progress entry alongside the view state so the
    // standalone progress store doesn't leak across opens/closes.
    clearBookProgress(key);
    set((state) => {
      const viewStates = { ...state.viewStates };
      delete viewStates[key];
      return { viewStates };
    });
    // A streamed ABS ebook reads through a RemoteFile that authenticates with
    // a short-lived access token, so its cached BookDoc must not outlive the
    // last open view: the next open resolves the stream afresh against the
    // store's current token. Local books keep their cache for instant reopens.
    const id = key.split('-')[0]!;
    if (Object.keys(get().viewStates).some((k) => k.split('-')[0] === id)) return;
    const book = useLibraryStore.getState().getBookByHash(id);
    if (book && isAbsEbook(book)) useBookDataStore.getState().clearBookData(id);
  },
  getViewState: (key: string) => get().viewStates[key] || null,
  // Closed books keep their keys but drop their view states; failed ones load again.
  areBooksOpen: (ids: string[]) => {
    const { bookKeys, viewStates } = get();
    return (
      bookKeys.length > 0 &&
      bookKeys.every((key) => viewStates[key] && !viewStates[key].error) &&
      bookKeys.map((key) => key.split('-')[0]).join() === ids.join()
    );
  },
  initViewState: async (
    envConfig: EnvConfigType,
    id: string,
    key: string,
    isPrimary = true,
    reload = false,
  ) => {
    const booksData = useBookDataStore.getState().booksData;
    const bookData = booksData[id];
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          key: '',
          view: null,
          viewerKey: '',
          isPrimary: false,
          loading: true,
          inited: false,
          error: null,
          ribbonVisible: false,
          ttsEnabled: false,
          autoScrollEnabled: false,
          drawingEnabled: false,
          syncing: false,
          gridInsets: null,
          previewMode: false,
          viewSettings: null,
        },
      },
    }));
    try {
      const appService = await envConfig.getAppService();
      const { settings } = useSettingsStore.getState();
      const { getBookByHash, library } = useLibraryStore.getState();
      const book = getBookByHash(id);
      if (!book) {
        console.error(
          `Book ${id} not found in library (size=${library.length}); likely the in-memory entry was dropped by a library reload.`,
        );
        throw new Error('Book not found');
      }
      const isPseStream = !!book.url && isPseStreamFileName(book.url);
      const isFeed = !!book.url && isFeedBookUrl(book.url);
      let bookDoc = bookData?.bookDoc;
      let docFormat = bookData?.docFormat;
      let file: File | null = bookData?.file ?? null;
      // Per-book config and the third-party annotation module are pure IO
      // with no dependency on the document load. Kick them off here so their
      // round trips overlap with the content fetch / document parsing below
      // instead of serializing behind them. Every `await configPromise`
      // resolves to the same object.
      const configPromise = appService.loadBookConfig(book, settings);
      // Avoid an unhandled rejection if the open path fails before the
      // config awaits below are ever reached; the rejection still propagates
      // at those await sites.
      configPromise.catch(() => undefined);
      const annotationImportPromise = import('@/services/annotation');
      // Avoid an unhandled rejection if the open path throws before the
      // annotation section below ever awaits this import.
      annotationImportPromise.catch(() => undefined);
      // A comic's wide pages are cached in its config: those an open measured,
      // and those found as streamed pages load.
      const makeWidePages = (config: BookConfig): WidePagesOptions => ({
        known: config.widePages,
        onFound: (ids) => useBookDataStore.getState().setConfig(id, { widePages: ids }),
      });
      if (!bookDoc || (!isPseStream && !isFeed && !file) || reload) {
        console.log('Loading book', key);
        if (isPseStream) {
          const data = parsePseStreamFileName(book.url!);
          const doc = await openPseStreamBook(data, makeWidePages(await configPromise));
          bookDoc = doc.book;
          file = null;
        } else if (isFeed) {
          const { feedUrl } = parseFeedBookUrl(book.url!);
          // AppService publicly exposes the readFile/writeFile/exists surface of FileSystem.
          const fs = appService as unknown as FileSystem;
          bookDoc = await openFeedBookDoc(fs, book.hash, feedUrl, book.title);
          file = null;
        } else {
          // resolveNativeBookFilePath only reads `book` (it feeds the Rust
          // EPUB prefetch), so it can race the content load instead of
          // queueing behind it.
          const [content, nativeFilePath] = await Promise.all([
            appService.loadBookContent(book) as Promise<BookContent>,
            appService.resolveNativeBookFilePath(book).catch((err: unknown) => {
              console.warn('resolveNativeBookFilePath failed', err);
              return null;
            }),
          ]);
          file = content.file;
          const config = await configPromise;
          const doc = await new DocumentLoader(file, {
            nativeFilePath: nativeFilePath ?? undefined,
            widePages: makeWidePages(config),
          }).open();
          bookDoc = doc.book;
          docFormat = doc.format;
          if (doc.format === 'CBZ') config.widePages = getWidePages(bookDoc.sections);
        }
      }
      const config = await configPromise;
      // Import annotations from third-party readers on first open. The
      // module import was already kicked off above; providers still run
      // here so the merged config lands before the first render.
      if (bookDoc.metadata.identifier) {
        const { getAnnotationProviders } = await annotationImportPromise;
        for (const provider of getAnnotationProviders()) {
          if (provider.isAvailable(appService)) {
            const merged = await provider.importAnnotations(
              appService,
              bookDoc.metadata.identifier,
              config,
            );
            if (merged !== config) {
              Object.assign(config, merged);
              await appService.saveBookConfig(book, config, settings);
            }
          }
        }
      }
      // Filter out invalid booknotes
      config.booknotes = config.booknotes?.filter((booknote) => booknote.cfi) ?? [];
      // Load cached book navigation (TOC + section fragments) or compute and persist.
      // Feed manifests grow between opens, so feed books always recompute instead.
      if (book.format === 'EPUB' && bookDoc.rendition?.layout !== 'pre-paginated') {
        const cachedNav = isFeed ? null : await appService.loadBookNav(book);
        if (isBookNavCacheCurrent(cachedNav) && process.env.NODE_ENV === 'production') {
          hydrateBookNav(bookDoc, cachedNav);
        } else {
          const freshNav = await computeBookNav(bookDoc);
          hydrateBookNav(bookDoc, freshNav);
          if (!isFeed) {
            try {
              await appService.saveBookNav(book, freshNav);
            } catch (e) {
              console.warn('Failed to persist book nav cache:', e);
            }
          }
        }
      }
      await updateToc(
        bookDoc,
        config.viewSettings?.sortedTOC ?? false,
        config.viewSettings?.convertChineseVariant ?? 'none',
      );
      if (!bookDoc.metadata.title && file) {
        bookDoc.metadata.title = getBaseFilename(file.name);
      }
      book.sourceTitle = formatTitle(bookDoc.metadata.title);
      // Correct language codes mistakenly set with language names
      if (typeof bookDoc.metadata?.language === 'string') {
        if (bookDoc.metadata.language in SUPPORTED_LANGNAMES) {
          bookDoc.metadata.language = SUPPORTED_LANGNAMES[bookDoc.metadata.language]!;
        }
      }
      // Set the book's language for formerly imported books, newly imported books have this field set
      const primaryLanguage = getPrimaryLanguage(bookDoc.metadata.language);
      book.primaryLanguage = book.primaryLanguage ?? primaryLanguage;
      book.metadata = book.metadata ?? bookDoc.metadata;

      // Update series info from metadata if available and not already set on the book
      if (bookDoc.metadata.belongsTo?.series) {
        const belongsTo = bookDoc.metadata.belongsTo.series;
        const series = Array.isArray(belongsTo) ? belongsTo[0] : belongsTo;
        if (series) {
          book.metadata.series = book.metadata.series ?? formatTitle(series.name);
          book.metadata.seriesIndex =
            book.metadata.seriesIndex ?? parseSeriesPosition(series.position);
          book.metadata.seriesTotal =
            book.metadata.seriesTotal ?? (series.total ? parseInt(series.total, 10) : undefined);
        }
      }
      // TODO: uncomment this when we can ensure metaHash is correctly generated for all books
      // book.metaHash = book.metaHash ?? getMetadataHash(bookDoc.metadata);
      // PDF metaHash is salted with the original import filename (issue #5411),
      // which is lost after import — keep the value stamped at import time.
      if (book.format !== 'PDF' || !book.metaHash) {
        book.metaHash = getMetadataHash(bookDoc.metadata);
      }

      const isFixedLayout =
        bookDoc.rendition?.layout === 'pre-paginated' || FIXED_LAYOUT_FORMATS.has(book.format);
      const newBookData: BookData = { id, book, file, config, bookDoc, docFormat, isFixedLayout };
      useBookDataStore.setState((state) => ({
        booksData: {
          ...state.booksData,
          [id]: newBookData,
        },
      }));
      const configViewSettings = config.viewSettings!;
      const globalViewSettings = settings.globalViewSettings;
      set((state) => ({
        viewStates: {
          ...state.viewStates,
          [key]: {
            ...state.viewStates[key],
            key,
            view: null,
            viewerKey: `${key}-${uniqueId()}`,
            isPrimary,
            loading: false,
            inited: false,
            error: null,
            ribbonVisible: false,
            ttsEnabled: false,
            autoScrollEnabled: false,
            drawingEnabled: false,
            syncing: false,
            gridInsets: null,
            previewMode: false,
            viewSettings: { ...globalViewSettings, ...configViewSettings },
          },
        },
      }));
    } catch (error) {
      console.error(error);
      set((state) => ({
        viewStates: {
          ...state.viewStates,
          [key]: {
            ...state.viewStates[key],
            key: '',
            view: null,
            viewerKey: '',
            isPrimary: false,
            loading: false,
            inited: false,
            error: 'Failed to load book.',
            ribbonVisible: false,
            ttsEnabled: false,
            autoScrollEnabled: false,
            drawingEnabled: false,
            syncing: false,
            gridInsets: null,
            previewMode: false,
            viewSettings: null,
          },
        },
      }));
      throw error;
    }
  },
  getViewSettings: (key: string) => get().viewStates[key]?.viewSettings || null,
  setViewSettings: (key: string, viewSettings: ViewSettings) => {
    if (!key) return;
    const id = key.split('-')[0]!;
    const bookData = useBookDataStore.getState().booksData[id];
    const viewState = get().viewStates[key];
    if (!viewState || !bookData) return;
    if (viewState.isPrimary) {
      useBookDataStore.setState((state) => ({
        booksData: {
          ...state.booksData,
          [id]: {
            ...bookData,
            config: {
              ...bookData.config,
              updatedAt: Date.now(),
              // The sidebar tab is written to the book config only, so keep
              // it rather than the live copy's stale value (#6836).
              viewSettings: {
                ...viewSettings,
                sideBarTab: bookData.config?.viewSettings?.sideBarTab ?? viewSettings.sideBarTab,
              },
            },
          },
        },
      }));
    }
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          viewSettings,
        },
      },
    }));
  },
  // Delegates to the standalone readerProgressStore so that progress reads
  // do not subscribe the caller to readerStore. Most call sites need a
  // one-shot read (event handlers, useEffect bodies). Components that
  // genuinely depend on progress for rendering should subscribe via the
  // `useBookProgress(key)` hook exported from readerProgressStore instead.
  getProgress: (key: string) => getBookProgress(key),
  setProgress: (
    key: string,
    location: string,
    tocItem: TOCItem,
    pageItem: BookProgress['pageItem'],
    section: PageInfo,
    pageinfo: PageInfo,
    timeinfo: TimeInfo,
    range: Range,
    fraction: number,
  ) => {
    const id = key.split('-')[0]!;
    const bookData = useBookDataStore.getState().booksData[id];
    const viewState = get().viewStates[key];
    if (!viewState || !bookData) return;

    const pageInfo = bookData.isFixedLayout ? section : pageinfo;
    const progress: [number, number] = [pageInfo.current + 1, pageInfo.total];
    const progressPercentage = Math.round((progress[0] / progress[1]) * 100);

    // Lightweight library update — O(1) lookup, no array copy, no refreshGroups
    const { getBookByHash, updateBookProgress } = useLibraryStore.getState();
    const existingBook = getBookByHash(id);
    if (existingBook) {
      let newReadingStatus = existingBook.readingStatus;
      if (existingBook.readingStatus === 'unread') {
        newReadingStatus = undefined;
      }
      if (progressPercentage >= 100 && existingBook.readingStatus !== 'finished') {
        newReadingStatus = 'finished';
      }
      updateBookProgress(id, progress, newReadingStatus);
    }

    // Only the primary view persists progress into the shared bookData
    // config — secondary views in a parallel layout shouldn't overwrite
    // it. Skip the bookDataStore write entirely when not primary to spare
    // its subscribers a re-render.
    if (viewState.isPrimary) {
      useBookDataStore.setState((state) => {
        const existing = state.booksData[id];
        if (!existing) return state;
        return {
          booksData: {
            ...state.booksData,
            [id]: {
              ...existing,
              config: {
                ...existing.config,
                progress,
                location,
              } as BookConfig,
            },
          },
        };
      });
    }

    // Write progress to the standalone store. This is the only setState on
    // the hot swipe path that the previous implementation routed through
    // the (much bigger) readerStore — the split here is the whole point of
    // the refactor: components subscribing to `useReaderStore()` without a
    // selector will no longer re-render per page turn.
    setBookProgress(key, {
      location,
      sectionHref: tocItem?.href,
      sectionLabel: tocItem?.label,
      pageItem,
      section,
      pageinfo,
      timeinfo,
      fraction,
      index: section.current,
      range,
      page: pageInfo.current + 1,
    } as BookProgress);
  },
  setBookmarkRibbonVisibility: (key: string, visible: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          ribbonVisible: visible,
        },
      },
    })),

  setTTSEnabled: (key: string, enabled: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          ttsEnabled: enabled,
        },
      },
    })),

  setDrawingEnabled: (key: string, enabled: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          drawingEnabled: enabled,
        },
      },
    })),

  setAutoScrollEnabled: (key: string, enabled: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          autoScrollEnabled: enabled,
        },
      },
    })),

  setIsLoading: (key: string, loading: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          loading,
        },
      },
    })),

  setIsSyncing: (key: string, syncing: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          syncing,
        },
      },
    })),

  getGridInsets: (key: string) =>
    get().viewStates[key]?.gridInsets || { top: 0, right: 0, bottom: 0, left: 0 },
  setGridInsets: (key: string, insets: Insets | null) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          gridInsets: insets,
        },
      },
    })),

  setViewInited: (key: string, inited: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          inited,
        },
      },
    })),

  setPreviewMode: (key: string, previewMode: boolean) =>
    set((state) => ({
      viewStates: {
        ...state.viewStates,
        [key]: {
          ...state.viewStates[key]!,
          previewMode,
        },
      },
    })),

  recreateViewer: (envConfig: EnvConfigType, key: string) => {
    if (!key || get().viewStates[key]?.key !== key) return;
    const id = key.split('-')[0]!;
    // `initViewState` already mints a fresh `viewerKey` when the reload lands,
    // which is what remounts <FoliateViewer>. Minting a second one here
    // remounted it twice: the abandoned first mount kept running its async
    // `openBook()` and registered another `data` transform listener on the
    // *same* reloaded bookDoc, so every resource was piped through the
    // transform chain twice. A twice-transformed stylesheet lost all its
    // font-family declarations, and the book fell back to the app font
    // (readest#5277).
    void get().initViewState(envConfig, id, key, true, true);
  },
}));
